import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtError, fmtDateTime } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FloppyDisk, ArrowUpRight } from "@phosphor-icons/react";

/**
 * /reporting-numbers — table of campaigns with reporting_numbers / mode / KPI.
 * Access: super_admin (edit) · delivery + delivery_head (edit own/assigned) · reporting (view only).
 */
export default function ReportingNumbers() {
    const { user } = useAuth();
    const canEdit = ["super_admin", "delivery", "delivery_head"].includes(user?.role);
    const [items, setItems] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState({});
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 20;

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/reporting-numbers");
            setItems(Array.isArray(data) ? data : []);
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const draft = (id, key) => drafts[id]?.[key] ?? (items.find((c) => c.id === id)?.[key] || "");
    const setDraft = (id, key, val) => setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: val } }));

    const isDirty = (id) => {
        const d = drafts[id]; if (!d) return false;
        const item = items.find((c) => c.id === id) || {};
        return ["reporting_numbers", "mode", "kpi"].some((k) => d[k] !== undefined && d[k] !== (item[k] || ""));
    };

    const save = async (id) => {
        const d = drafts[id] || {};
        setSaving((s) => ({ ...s, [id]: true }));
        try {
            await api.patch(`/campaigns/${id}/reporting-meta`, {
                reporting_numbers: d.reporting_numbers ?? null,
                mode: d.mode ?? null,
                kpi: d.kpi ?? null,
            });
            toast.success("Saved");
            setDrafts((prev) => { const c = { ...prev }; delete c[id]; return c; });
            load();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
        finally { setSaving((s) => ({ ...s, [id]: false })); }
    };

    const q = query.trim().toLowerCase();
    const filtered = q
        ? items.filter((c) =>
            [c.name, c.reporting_numbers, c.mode, c.kpi]
                .some((v) => (v || "").toString().toLowerCase().includes(q)))
        : items;
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <div className="p-6 md:p-10" data-testid="reporting-numbers-page">
            <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline">Reporting</div>
                    <h1 className="font-display text-3xl">Reporting numbers</h1>
                    <p className="text-sm text-slate-500 mt-2">
                        {canEdit
                            ? "Set the reporting number, mode, and KPI for each campaign. Delivery + Super Admin can edit; Reporting has view-only access."
                            : "View-only. Delivery / Super Admin maintain these values."}
                    </p>
                </div>
                <div className="w-full md:w-80">
                    <Input
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                        placeholder="Search by campaign, mode, KPI…"
                        data-testid="reporting-search"
                        className="rounded-none border-slate-300 h-9"
                    />
                </div>
            </div>

            {loading && <p className="text-xs font-mono text-slate-400">loading…</p>}

            {!loading && items.length === 0 && (
                <div className="border border-dashed border-slate-300 p-16 text-center">
                    <p className="text-sm text-slate-500">No campaigns visible for your role.</p>
                </div>
            )}

            {!loading && items.length > 0 && filtered.length === 0 && (
                <div className="border border-dashed border-slate-300 p-8 text-center">
                    <p className="text-sm text-slate-500">No campaigns match &ldquo;{query}&rdquo;.</p>
                </div>
            )}

            {!loading && filtered.length > 0 && (
                <div className="border border-slate-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm table-auto">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="text-left px-3 py-2 overline">Campaign</th>
                                <th className="text-left px-3 py-2 overline">Reporting numbers</th>
                                <th className="text-left px-3 py-2 overline">Mode</th>
                                <th className="text-left px-3 py-2 overline">KPI</th>
                                <th className="text-left px-3 py-2 overline whitespace-nowrap">Last update</th>
                                {canEdit && <th className="text-right px-3 py-2 overline">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {paged.map((c) => (
                                <tr key={c.id} className="border-t border-slate-200 hover:bg-slate-50/50" data-testid={`reporting-row-${c.id}`}>
                                    <td className="px-3 py-2">
                                        <Link to={`/campaigns/${c.id}`} className="inline-flex items-center gap-1 font-medium text-slate-900 hover:underline">
                                            {c.name} <ArrowUpRight size={11} weight="bold" />
                                        </Link>
                                        <div className="font-mono text-[10px] text-slate-500 mt-0.5">stage · {c.stage}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        {canEdit ? (
                                            <Input
                                                value={draft(c.id, "reporting_numbers")}
                                                onChange={(e) => setDraft(c.id, "reporting_numbers", e.target.value)}
                                                data-testid={`reporting-numbers-input-${c.id}`}
                                                className="rounded-none border-slate-300 h-9 font-mono text-xs"
                                            />
                                        ) : <span className="font-mono text-xs">{c.reporting_numbers || "—"}</span>}
                                    </td>
                                    <td className="px-3 py-2">
                                        {canEdit ? (
                                            <Input
                                                value={draft(c.id, "mode")}
                                                onChange={(e) => setDraft(c.id, "mode", e.target.value)}
                                                data-testid={`reporting-mode-input-${c.id}`}
                                                className="rounded-none border-slate-300 h-9 font-mono text-xs"
                                            />
                                        ) : <span className="font-mono text-xs">{c.mode || "—"}</span>}
                                    </td>
                                    <td className="px-3 py-2">
                                        {canEdit ? (
                                            <Input
                                                value={draft(c.id, "kpi")}
                                                onChange={(e) => setDraft(c.id, "kpi", e.target.value)}
                                                data-testid={`reporting-kpi-input-${c.id}`}
                                                className="rounded-none border-slate-300 h-9 font-mono text-xs"
                                            />
                                        ) : <span className="font-mono text-xs">{c.kpi || "—"}</span>}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500 whitespace-nowrap">
                                        {c.reporting_meta_updated_at ? fmtDateTime(c.reporting_meta_updated_at) : "—"}
                                    </td>
                                    {canEdit && (
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <Button
                                                onClick={() => save(c.id)}
                                                disabled={!isDirty(c.id) || saving[c.id]}
                                                data-testid={`reporting-save-${c.id}`}
                                                className="rounded-none bg-slate-900 text-white hover:bg-slate-800 h-8 gap-1 text-[10px] font-mono uppercase tracking-wider disabled:opacity-40"
                                            >
                                                <FloppyDisk size={12} weight="bold" /> Save
                                            </Button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!loading && filtered.length > PAGE_SIZE && (
                <div className="mt-4 flex items-center justify-between text-xs font-mono text-slate-600" data-testid="reporting-pagination">
                    <div>
                        {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            disabled={currentPage === 1}
                            onClick={() => setPage(currentPage - 1)}
                            data-testid="reporting-page-prev"
                            className="px-3 py-1.5 border border-slate-300 uppercase tracking-wider hover:bg-slate-50 disabled:opacity-40"
                        >Prev</button>
                        <span className="px-3 py-1.5 border border-slate-900 bg-slate-900 text-white">{currentPage} / {totalPages}</span>
                        <button
                            type="button"
                            disabled={currentPage === totalPages}
                            onClick={() => setPage(currentPage + 1)}
                            data-testid="reporting-page-next"
                            className="px-3 py-1.5 border border-slate-300 uppercase tracking-wider hover:bg-slate-50 disabled:opacity-40"
                        >Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}
